import { DollarSign } from 'lucide-react';
import { SECTION_CONFIGS } from '../../constants/sectionConfig.constants';
import type { Application } from '../../types/application.types';
import { CollapsibleSection } from '../shared/CollapsibleSection';
import { FinancialBreakdown } from '../shared/FinancialBreakdown';
import { PricingTable } from '../shared/PricingTable';

interface FinancialStepProps {
  application: Application;
}

export function FinancialStep({ application }: FinancialStepProps) {
  const config = SECTION_CONFIGS.financier;

  // Utiliser filteredPriceBreakdown si disponible, sinon fallback sur les anciens champs
  const hasPriceBreakdown = application.filteredPriceBreakdown && application.filteredPriceBreakdown.length > 0;
  const hasLegacyData =
    application.totalAmount !== undefined ||
    application.baseAmount !== undefined ||
    application.optionsAmount !== undefined;

  const hasFinancialData = hasPriceBreakdown || hasLegacyData;

  // Si aucune donnée financière
  if (!hasFinancialData) {
    return (
      <div className="space-y-4">
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 text-center">
          <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">Aucune information financière disponible</p>
        </div>
      </div>
    );
  }

  // Utiliser filteredPriceBreakdown en priorité
  let priceItems: Array<{ label: string; amount: number; name: string; duration: string }> = [];
  let totalAmount = application.totalAmount;

  if (hasPriceBreakdown) {
    // Utiliser le priceBreakdown filtré (sans taxes) - déjà avec name et duration
    priceItems = application.filteredPriceBreakdown!;
    // Calculer le total à partir des items (sans taxes)
    totalAmount = priceItems.reduce((sum, item) => sum + item.amount, 0);
  } else {
    // Fallback sur l'ancienne structure
    if (application.baseAmount !== undefined) {
      priceItems.push({
        label: 'Montant de base',
        amount: application.baseAmount,
        name: 'Montant de base',
        duration: 'N/A',
      });
    }

    if (application.optionsAmount !== undefined && application.optionsAmount > 0) {
      priceItems.push({
        label: 'Options et services',
        amount: application.optionsAmount,
        name: 'Options et services',
        duration: 'N/A',
      });
    }
  }

  return (
    <div className="space-y-4">
      <CollapsibleSection title={config.title} color={config.color} icon={config.icon} defaultOpen={config.defaultOpen}>
        {hasPriceBreakdown ? (
          <PricingTable items={priceItems} total={totalAmount} currency={application.currency || 'FCFA'} />
        ) : (
          <FinancialBreakdown items={priceItems} total={totalAmount} />
        )}
      </CollapsibleSection>
    </div>
  );
}
